/**
 * @fileoverview SkeletonButton — CTA/button shimmer.
 * @package @puffin-core/skeleton-kit
 */

import { memo } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {object} SkeletonButtonOwnProps
 * @property {string} [width] - A CSS length.
 * @property {boolean} [animated=true]
 * @property {string} [className]
 */

/**
 * The remaining props (an `aria-*` override, a test id) are spread onto the element.
 *
 * @typedef {SkeletonButtonOwnProps & React.HTMLAttributes<HTMLDivElement>} SkeletonButtonProps
 */

/**
 * Memoised; it keeps its `propTypes` declaration until JSDOC-PROP-CONTRACT-001 Phase 3 retires it.
 *
 * @type {React.NamedExoticComponent<SkeletonButtonProps> & {propTypes?: object}}
 */
export const SkeletonButton = memo(
  /**
   * @param {SkeletonButtonProps} props
   * @returns {React.JSX.Element}
   */
  function SkeletonButton({ width, animated = true, className = '', ...props }) {
    const style = width ? { width } : undefined;
    const classes = ['skeleton', 'skeleton-button', animated ? null : 'skeleton-static', className]
      .filter(Boolean)
      .join(' ')
      .trim();

    return <div className={classes} aria-hidden="true" style={style} {...props} />;
  },
);

SkeletonButton.propTypes = {
  width: PropTypes.string,
  animated: PropTypes.bool,
  className: PropTypes.string,
};

export default SkeletonButton;
